import type { RunSortField, RunStatus } from '@tms/shared';
import { RUN_STATUSES } from '@tms/shared';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  ArrowDownUp,
  Columns3,
  FilterX,
  PlayCircle,
  Plus,
  RotateCw,
  RotateCcw,
  SlidersHorizontal,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { PageHeader } from '../../components/layout/PageHeader';
import {
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  EntityKey,
  ErrorState,
  LabelChip,
  Menu,
  MenuCheckboxItem,
  MenuItem,
  MenuLabel,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSeparator,
  Pagination,
  ResultBar,
  RunStatusBadge,
  SearchField,
  SkeletonRows,
  Table,
  TableWrap,
  ToolbarMenu,
  Td,
  Th,
  Tr,
} from '../../components/ui';
import { useEnumLabels, useErrorText, useFormat, useT, type MessageKey } from '../../i18n';
import { useDebounced } from '../../lib/use-debounced';
import { usePermissions } from '../auth/permissions';
import { FolderTree, type FolderSelection } from '../folders/FolderTree';
import { useUsers } from '../users/api';
import { CreateRunDialog } from './CreateRunDialog';
import { useDeleteTestRun, useRerunTestRun, useTestRuns } from './api';
import { RUN_COLUMNS, RUN_COLUMN_LABELS, useRunColumns, type RunColumn } from './columns';

/** Radix radio groups work on strings, so "no filter" needs its own value. */
const ANY = 'any';

type SortValue = `${RunSortField}:${'asc' | 'desc'}`;

/** The same ordering the other two lists offer, over the run's own keys. */
const SORT_OPTIONS: Array<{ value: SortValue; labelKey: MessageKey }> = [
  { value: 'seqNumber:desc', labelKey: 'cases.sortNewest' },
  { value: 'seqNumber:asc', labelKey: 'cases.sortOldest' },
  { value: 'title:asc', labelKey: 'cases.sortTitleAsc' },
  { value: 'title:desc', labelKey: 'cases.sortTitleDesc' },
  { value: 'updatedAt:desc', labelKey: 'cases.sortUpdated' },
];

/**
 * The columns that hold wrapping text get room, as a minimum the title column
 * cannot take back: the table lays itself out automatically, so a plain width
 * would be squeezed to the longest word. The date column stays narrow on
 * purpose — the time reads under the date instead of stretching the row.
 */
const RUN_COLUMN_WIDTHS: Record<RunColumn, string> = {
  checklist: 'min-w-56',
  status: 'min-w-28',
  labels: 'min-w-32',
  result: 'min-w-44',
  environment: 'min-w-28',
  assignee: 'min-w-32',
  created: 'w-24',
};

export const TestRunsPage = ({ projectCode }: { projectCode: string }) => {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const { t } = useT();
  const { date, time } = useFormat();
  const { runStatus } = useEnumLabels();
  const { errorText } = useErrorText();

  const columns = useRunColumns();
  const [folder, setFolder] = useState<FolderSelection>('all');
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<RunStatus | ''>('');
  const [assigneeId, setAssigneeId] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortValue>('seqNumber:desc');
  const [createOpen, setCreateOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<{ id: string; runKey: string } | null>(null);

  const debouncedSearch = useDebounced(search);
  const { data, isPending, error, refetch } = useTestRuns(projectCode, {
    page,
    ...(folder === 'all' ? {} : { folderId: folder, includeSubfolders: true }),
    ...(status ? { status } : {}),
    ...(assigneeId ? { assigneeId } : {}),
    ...(debouncedSearch ? { q: debouncedSearch } : {}),
    sort: sort.split(':')[0] as RunSortField,
    order: sort.split(':')[1] as 'asc' | 'desc',
  });

  const activeSort = SORT_OPTIONS.find((option) => option.value === sort) ?? SORT_OPTIONS[0]!;
  const { data: users } = useUsers({ perPage: 100 });
  const activeFilterCount = (status ? 1 : 0) + (assigneeId ? 1 : 0);

  const rerun = useRerunTestRun(projectCode);
  const remove = useDeleteTestRun(projectCode);

  const onRerun = async (runId: string) => {
    try {
      const created = await rerun.mutateAsync({ runId, input: {} });
      toast.success(t('runs.rerunCreatedShort', { key: created.runKey }));
      await navigate({
        to: '/projects/$projectCode/test-runs/$runKey',
        params: { projectCode, runKey: created.runKey },
      });
    } catch (rerunError) {
      toast.error(errorText(rerunError, 'runs.rerunFailed'));
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await remove.mutateAsync(pendingDelete.id);
      toast.success(t('runs.deleted', { key: pendingDelete.runKey }));
      setPendingDelete(null);
    } catch (deleteError) {
      toast.error(errorText(deleteError, 'runs.deleteFailed'));
    }
  };

  return (
    <>
      <PageHeader
        title={t('runs.title')}
        description={t('runs.subtitle')}
        actions={
          can('run:create') ? (
            <Button variant="primary" onClick={() => setCreateOpen(true)}>
              <Plus className="size-4" />
              {t('runs.new')}
            </Button>
          ) : null
        }
      />

      <div className="flex flex-col gap-4 lg:flex-row">
        <FolderTree
          projectCode={projectCode}
          selected={folder}
          onSelect={(selection) => {
            setFolder(selection);
            setPage(1);
          }}
          total={data?.meta.total ?? 0}
          kind="run"
          allLabel={t('folders.allRuns')}
        />

        <Card className="min-w-0 flex-1">
        <div className="flex items-center gap-2 border-b border-border px-4 py-2.5">
          <SearchField
            value={search}
            onChange={(value) => {
              setSearch(value);
              setPage(1);
            }}
            placeholder={t('runs.searchPlaceholder')}
            label={t('runs.searchLabel')}
          />

          <ToolbarMenu
            icon={<SlidersHorizontal className="size-3.5" />}
            label={t('common.filters')}
            badge={activeFilterCount}
            compact
          >
            <MenuLabel>{t('common.status')}</MenuLabel>
            <MenuRadioGroup
              value={status === '' ? ANY : status}
              onValueChange={(value) => {
                setStatus(value === ANY ? '' : (value as RunStatus));
                setPage(1);
              }}
            >
              <MenuRadioItem value={ANY} keepOpen>
                {t('runs.allStatuses')}
              </MenuRadioItem>
              {RUN_STATUSES.map((value) => (
                <MenuRadioItem key={value} value={value} keepOpen>
                  {runStatus(value)}
                </MenuRadioItem>
              ))}
            </MenuRadioGroup>

            <MenuSeparator />

            <MenuLabel>{t('runs.columnAssignee')}</MenuLabel>
            <MenuRadioGroup
              value={assigneeId === '' ? ANY : assigneeId}
              onValueChange={(value) => {
                setAssigneeId(value === ANY ? '' : value);
                setPage(1);
              }}
            >
              <MenuRadioItem value={ANY} keepOpen>
                {t('runs.allAssignees')}
              </MenuRadioItem>
              {(users?.data ?? []).map((user) => (
                <MenuRadioItem key={user.id} value={user.id} keepOpen>
                  {user.name}
                </MenuRadioItem>
              ))}
            </MenuRadioGroup>

            {activeFilterCount > 0 ? (
              <>
                <MenuSeparator />
                <MenuItem
                  onSelect={() => {
                    setStatus('');
                    setAssigneeId('');
                    setPage(1);
                  }}
                >
                  <FilterX className="size-3.5" />
                  {t('common.resetFilters')}
                </MenuItem>
              </>
            ) : null}
          </ToolbarMenu>

          <ToolbarMenu
            icon={<Columns3 className="size-3.5" />}
            label={t('cases.columns')}
            compact
          >
            <MenuLabel>{t('cases.columns')}</MenuLabel>
            {RUN_COLUMNS.map((column) => (
              <MenuCheckboxItem
                key={column}
                checked={columns.isVisible(column)}
                onCheckedChange={(checked) => columns.toggle(column, checked)}
              >
                {t(RUN_COLUMN_LABELS[column])}
              </MenuCheckboxItem>
            ))}
            {columns.isDefault ? null : (
              <>
                <MenuSeparator />
                <MenuItem onSelect={columns.reset}>
                  <RotateCcw className="size-3.5" />
                  {t('cases.resetColumns')}
                </MenuItem>
              </>
            )}
          </ToolbarMenu>

          <ToolbarMenu
            icon={<ArrowDownUp className="size-3.5" />}
            label={t(activeSort.labelKey)}
            compact
          >
            <MenuLabel>{t('common.sort')}</MenuLabel>
            <MenuRadioGroup value={sort} onValueChange={(value) => setSort(value as SortValue)}>
              {SORT_OPTIONS.map((option) => (
                <MenuRadioItem key={option.value} value={option.value}>
                  {t(option.labelKey)}
                </MenuRadioItem>
              ))}
            </MenuRadioGroup>
          </ToolbarMenu>
        </div>

        {error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : isPending ? (
          <SkeletonRows />
        ) : data.data.length === 0 ? (
          <EmptyState
            icon={<PlayCircle className="size-6" />}
            title={
              debouncedSearch || status || assigneeId ? t('runs.emptyFiltered') : t('runs.empty')
            }
            description={
              debouncedSearch || status || assigneeId
                ? t('runs.emptyFilteredHint')
                : t('runs.emptyHint')
            }
            action={
              can('run:create') && !debouncedSearch && !status && !assigneeId ? (
                <Button variant="primary" onClick={() => setCreateOpen(true)}>
                  <Plus className="size-4" />
                  {t('runs.create')}
                </Button>
              ) : null
            }
          />
        ) : (
          <>
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <Th className="w-28">{t('cases.columnKey')}</Th>
                    <Th className="w-full">{t('runs.columnRun')}</Th>
                    {columns.visible.map((column) => (
                      <Th key={column} className={RUN_COLUMN_WIDTHS[column]}>
                        {t(RUN_COLUMN_LABELS[column])}
                      </Th>
                    ))}
                    <Th className="w-10" />
                  </tr>
                </thead>
                <tbody>
                  {data.data.map((run) => (
                    <Tr
                      key={run.id}
                      onClick={() =>
                        void navigate({
                          to: '/projects/$projectCode/test-runs/$runKey',
                          params: { projectCode, runKey: run.runKey },
                        })
                      }
                    >
                      <Td>
                        <EntityKey>{run.runKey}</EntityKey>
                      </Td>
                      <Td>
                        <Link
                          to="/projects/$projectCode/test-runs/$runKey"
                          params={{ projectCode, runKey: run.runKey }}
                          className="font-medium text-ink hover:text-accent-fg"
                          onClick={(event) => event.stopPropagation()}
                        >
                          {run.title}
                        </Link>
                      </Td>
                      {columns.isVisible('checklist') ? (
                        <Td className="text-ink-muted">
                          {run.checklists.length === 0 ? (
                            t('common.empty')
                          ) : (
                            // A run can cover several, one per line.
                            <span className="flex flex-col gap-0.5">
                              {run.checklists.map((source, index) => (
                                <span key={`${source.id ?? 'deleted'}-${index}`}>
                                  {source.title}
                                </span>
                              ))}
                            </span>
                          )}
                        </Td>
                      ) : null}
                      {columns.isVisible('status') ? (
                        <Td>
                          <RunStatusBadge status={run.status} />
                        </Td>
                      ) : null}
                      {columns.isVisible('labels') ? (
                        <Td>
                          {run.labels.length === 0 ? (
                            <span className="text-ink-subtle">{t('common.empty')}</span>
                          ) : (
                            <span className="flex flex-wrap gap-1">
                              {run.labels.map((label) => (
                                <LabelChip key={label.id} label={label} />
                              ))}
                            </span>
                          )}
                        </Td>
                      ) : null}
                      {columns.isVisible('result') ? (
                        <Td>
                          <ResultBar
                            passed={run.stats.passed}
                            failed={run.stats.failed}
                            blocked={run.stats.blocked}
                            skipped={run.stats.skipped}
                            total={run.stats.total}
                          />
                          <p className="mt-1 flex gap-2 text-[12px] tabular-nums">
                            <span className="text-pass">
                              {t('runs.resultsPassed', {
                                passed: run.stats.passed,
                                total: run.stats.total,
                              })}
                            </span>
                            {run.stats.failed > 0 ? (
                              <span className="text-fail">
                                {t('runs.resultsFailed', { count: run.stats.failed })}
                              </span>
                            ) : null}
                          </p>
                        </Td>
                      ) : null}
                      {columns.isVisible('environment') ? (
                        <Td className="text-ink-muted">
                          {run.environment?.name ?? t('common.empty')}
                        </Td>
                      ) : null}
                      {columns.isVisible('assignee') ? (
                        <Td className="text-ink-muted">
                          {run.assignee?.name ?? t('common.unassigned')}
                        </Td>
                      ) : null}
                      {columns.isVisible('created') ? (
                        <Td className="text-ink-muted">
                          {/* Narrow on purpose: the time reads under the date. */}
                          <span className="block whitespace-nowrap">{date(run.createdAt)}</span>
                          <span className="block whitespace-nowrap text-[12px] text-ink-subtle">
                            {time(run.createdAt)}
                          </span>
                        </Td>
                      ) : null}
                      <Td>
                        <div onClick={(event) => event.stopPropagation()}>
                          <Menu>
                            {can('run:create') ? (
                              <MenuItem onSelect={() => void onRerun(run.id)}>
                                <RotateCw className="size-3.5" />
                                {t('runs.rerun')}
                              </MenuItem>
                            ) : null}
                            {can('run:delete') ? (
                              <MenuItem
                                destructive
                                disabled={run.status !== 'active'}
                                onSelect={() => setPendingDelete({ id: run.id, runKey: run.runKey })}
                              >
                                <Trash2 className="size-3.5" />
                                {t('runs.deleteAction')}
                              </MenuItem>
                            ) : null}
                          </Menu>
                        </div>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
            <Pagination meta={data.meta} onPageChange={setPage} />
          </>
        )}
        </Card>
      </div>

      <CreateRunDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        projectCode={projectCode}
        defaultFolderId={folder === 'all' ? undefined : folder}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title={t('runs.deleteTitle', { key: pendingDelete?.runKey ?? '' })}
        description={t('runs.deleteHint')}
        confirmLabel={t('runs.deleteAction')}
        loading={remove.isPending}
        onConfirm={() => void confirmDelete()}
      />
    </>
  );
};
